import { Box, Icon, Stack } from '@chakra-ui/react';
import { IoMdPaper } from 'react-icons/io';
import { useNavigate } from 'react-router';
import FaceliftBadge from '@/components/Atoms/FaceliftBadge/FaceliftBadge';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import DetailsView from '@/components/Templates/DetailsView';
import { formatDate } from '@/utils/dateUtils';
import { buildDocumentsUrl } from '@/utils/navigationItems';

interface PreviewProps {
  bvInformation: {
    clinicName: string;
    clinic: string;
    portalBVUploadUrl: string;
    providerName: string;
    patientName: string;
    birthdate: string;
    primaryInsurance: string;
    secondaryInsurance: string;
    primaryInsuranceMemberID: string;
    secondaryInsuranceMemberID: string;
    emrId: string;
    isPatientInNursingHome: string;
    hasPatientHad100DaysOfCare: string;
    noteshartreview: string;
    productSelection: string;
    productStatuses?: string;
    procedureDate: string;
    wounds: {
      woundLength: number;
      woundWidth: number;
      woundDepth: number;
      woundType: string;
      applicationCode1: string;
      applicationCode2: string;
      dCode1: string;
      dCode2: string;
      dCode3: string;
      dCode4: string;
    }[];
    totalWoundsCreated: number;
    uploadedFiles: string[];
  };
  isCreationMode?: boolean;
  onNavigateToDoc?: () => void;
}

const Preview: React.FC<PreviewProps> = ({ bvInformation, isCreationMode = false, onNavigateToDoc }) => {
  const navigate = useNavigate();

  const navigateToDoc = () => {
		// Don't navigate if in creation mode (before submit)
		if (isCreationMode) {
			return;
		}
		
		// If a custom navigation handler is provided (e.g., from BGProcess), use it
		if (onNavigateToDoc) {
			onNavigateToDoc();
			return;
		}
		
		const url = String(bvInformation.portalBVUploadUrl) || '';
		if (url && bvInformation.clinic) {
			const documentsUrl = buildDocumentsUrl(
				url,
				bvInformation.clinic,
				bvInformation.clinicName,
			);
			if (documentsUrl) {
				navigate(documentsUrl);
			}
		}
	};
  return (
    <Box>
      <Stack gap={8} maxH={700}
        style={{
          overflowY: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Facility Information
          </FaceliftText>
          <DetailsView
            gridConfig={{ base: '3' }}
            gap={{ md: 12 }}
            data={[
              {
                data: [
                  {
                    label: 'Clinic Name',
                    value: bvInformation?.clinicName || '---',
                  },
                ],
              },
              {
                data: [
                  {
                    label: 'Provider',
                    value: bvInformation?.providerName || '---',
                  },
                ],
              },
              { data: [{ label: 'Status', value: 'Active' }] },
            ]}
          />
        </Box>
        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Patient Information
          </FaceliftText>
          <DetailsView
            gridConfig={{ base: '3' }}
            gap={{ md: 12 }}
            data={[
              {
                data: [
                  {
                    label: 'Patient',
                    value: bvInformation?.patientName || '---',
                  },
                  { label: 'Primary Insurance', value: bvInformation?.primaryInsurance || '---' },
                  {
                    label: 'Primary Member ID',
                    value: bvInformation?.primaryInsuranceMemberID || '---',
                  },
                ],
              },
              {
                data: [
                  { label: 'DOB', value: bvInformation?.birthdate ? formatDate(bvInformation?.birthdate) : '---' },
                  {
                    label: 'Secondary Insurance',
                    value: bvInformation?.secondaryInsurance || '---',
                  },
                  {
                    label: 'Secondary Member ID',
                    value: bvInformation?.secondaryInsuranceMemberID || '---',
                  },
                ],
              },
              {
                data: [
                  {
                    // label: 'Primary Insurance',
                    label: 'EMRID',
                    value: bvInformation?.emrId || '---',
                  },
                  {
                    label: 'Is Patient Residing in a Nursing Home or Facility?',
                    value: bvInformation?.isPatientInNursingHome || 'No',
                  },
                  {
                    label: 'If Yes, has it been over 100 days',
                    value: bvInformation?.hasPatientHad100DaysOfCare || 'No',
                  },
                ],
              },
            ]}
          />
        </Box>

        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Product(s) Selected
          </FaceliftText>

          <Stack direction={'row'} flexWrap={'wrap'}>
            {(isCreationMode ? bvInformation?.productSelection : bvInformation?.productStatuses)?.split(',').map((product) => (
              <Box key={product} display={'flex'} flexDirection={'column'} gap={1}>
                {product.split(':').map((part, index) => {
                  return <FaceliftBadge
                    key={index + part}
                    status={part.trim()}
                    badgeColor={index === 0 ? "brand.solid" : "gray"}
                  />
                })}
              </Box>
            ))}
          </Stack>
        </Box>

        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Notes
          </FaceliftText>
          <FaceliftText>{bvInformation?.noteshartreview || '---'}</FaceliftText>
        </Box>

        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Wound 1
          </FaceliftText>
          <DetailsView
            gridConfig={{ base: '3' }}
            gap={{ md: 12 }}
            data={[
              {
                data: [
                  {
                    label: 'Procedure Date',
                    value: bvInformation?.procedureDate ? formatDate(bvInformation?.procedureDate) : '---',
                  },
                  {
                    label: 'Application Code 1',
                    value:
                      bvInformation?.wounds?.at(0)?.applicationCode1 || '---',
                  },
                  {
                    label: 'Application Code 2',
                    value:
                      bvInformation?.wounds?.at(0)?.applicationCode2 || '---',
                  },
                ],
              },
              {
                data: [
                  {
                    label: 'Wound Length cm',
                    value:
                      bvInformation?.wounds?.at(0)?.woundLength?.toString() ||
                      '---',
                  },
                  {
                    label: 'Wound Width cm',
                    value:
                      bvInformation?.wounds?.at(0)?.woundWidth?.toString() ||
                      '---',
                  },
                  {
                    label: 'Diagnosis Code 2',
                    value: bvInformation?.wounds?.at(0)?.dCode2 || '---',
                  },
                ],
              },
              {
                data: [
                  {
                    label: 'Primary Diagnosis',
                    value: bvInformation?.wounds?.at(0)?.dCode1 || '---',
                  },
                  {
                    label: 'Wound Depth cm',
                    value:
                      bvInformation?.wounds?.at(0)?.woundDepth?.toString() ||
                      '---',
                  },
                  {
                    label: 'Diagnosis Code 3',
                    value: bvInformation?.wounds.at(0)?.dCode3 || '---',
                  },
                ],
              },
            ]}
          />
        </Box>

        <Show when={bvInformation?.totalWoundsCreated > 1}>
          <Box>
            <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
              Wound 2
            </FaceliftText>
            <DetailsView
              gridConfig={{ base: '3' }}
              gap={{ md: 12 }}
              data={[
                {
                  data: [
                    {
                      label: 'Wound Length cm',
                      value:
                        bvInformation?.wounds?.at(1)?.woundLength?.toString() ||
                        '---',
                    },
                    {
                      label: 'Application Code 1',
                      value:
                        bvInformation?.wounds?.at(1)?.applicationCode1 || '---',
                    },
                    {
                      label: 'Application Code 2',
                      value:
                        bvInformation?.wounds?.at(1)?.applicationCode2 || '---',
                    },
                  ],
                },
                {
                  data: [
                    {
                      label: 'Wound Width cm',
                      value:
                        bvInformation?.wounds?.at(1)?.woundWidth?.toString() ||
                        '---',
                    },
                    {
                      label: 'Diagnosis Code 2',
                      value: bvInformation?.wounds?.at(1)?.dCode2 || '---',
                    },
                    {
                      label: 'Diagnosis Code 3',
                      value: bvInformation?.wounds?.at(1)?.dCode3 || '---',
                    },
                  ],
                },
                {
                  data: [
                    {
                      label: 'Primary Diagnosis',
                      value: bvInformation?.wounds?.at(1)?.dCode1 || '---',
                    },
                    {
                      label: 'Wound Depth cm',
                      value:
                        bvInformation?.wounds?.at(1)?.woundDepth?.toString() ||
                        '---',
                    },
                  ],
                },
              ]}
            />
          </Box>
        </Show>

        <Show when={bvInformation?.totalWoundsCreated > 2}>
          <Box>
            <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
              Wound 3
            </FaceliftText>
            <DetailsView
              gridConfig={{ base: '3' }}
              gap={{ md: 12 }}
              data={[
                {
                  data: [
                    {
                      label: 'Wound Length cm',
                      value:
                        bvInformation?.wounds?.at(2)?.woundLength?.toString() ||
                        '---',
                    },
                    {
                      label: 'Application Code 1',
                      value:
                        bvInformation?.wounds?.at(2)?.applicationCode1 || '---',
                    },
                    {
                      label: 'Application Code 2',
                      value:
                        bvInformation?.wounds?.at(2)?.applicationCode2 || '---',
                    },
                  ],
                },
                {
                  data: [
                    {
                      label: 'Wound Width cm',
                      value:
                        bvInformation?.wounds?.at(2)?.woundWidth?.toString() ||
                        '---',
                    },
                    {
                      label: 'Diagnosis Code 2',
                      value: bvInformation?.wounds?.at(2)?.dCode2 || '---',
                    },
                    {
                      label: 'Diagnosis Code 3',
                      value: bvInformation?.wounds?.at(2)?.dCode3 || '---',
                    },
                  ],
                },
                {
                  data: [
                    {
                      label: 'Primary Diagnosis',
                      value: bvInformation?.wounds?.at(2)?.dCode1 || '---',
                    },
                    {
                      label: 'Wound Depth cm',
                      value:
                        bvInformation?.wounds?.at(2)?.woundDepth?.toString() ||
                        '---',
                    },
                  ],
                },
              ]}
            />
          </Box>
        </Show>

        <Box>
          <FaceliftText fontWeight={'bold'} fontStyle={'italic'} mb={2}>
            Uploaded Files
          </FaceliftText>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={4}>
            {bvInformation.uploadedFiles?.map((file: string, index) => (
              <Stack 
                key={file} 
                direction={'row'} 
                alignItems={'center'} 
                onClick={isCreationMode ? undefined : navigateToDoc}
                cursor={isCreationMode ? 'default' : 'pointer'}
              >
                <Icon size={'xl'}>
                  <IoMdPaper />
                </Icon>
                <FaceliftText
                  fontStyle={'italic'}
                  _hover={isCreationMode ? {} : { color: 'brand.600', cursor: 'pointer' }}
                >
                  {file}
                </FaceliftText>
              </Stack>
            ))}
          </Box>
          <Show when={bvInformation.uploadedFiles?.length === 0 || !bvInformation.uploadedFiles}>
            <FaceliftText color={'gray.600'} fontWeight={'bold'}>No files uploaded.</FaceliftText>
          </Show>
        </Box>
      </Stack>
    </Box>
  );
};

export default Preview;
